import type { ModerationInfo } from './workspace-realtime.service';

/**
 * Tab-scoped marker of the moderation session this browser tab started.
 *
 * sessionStorage survives a reload of the same tab but is not shared with
 * other tabs or devices. A second session of the same account therefore sees
 * the running moderation as joinable instead of taking it over.
 */
const OWNED_MODERATION_SESSION_KEY =
  'streamlain:presentation:ownedModerationSessionId';

export function rememberOwnedModerationSession(sessionId: string): void {
  try {
    sessionStorage.setItem(OWNED_MODERATION_SESSION_KEY, sessionId);
  } catch {
    // Storage unavailable: the tab simply cannot resume after reload.
  }
}

export function forgetOwnedModerationSession(): void {
  try {
    sessionStorage.removeItem(OWNED_MODERATION_SESSION_KEY);
  } catch {
    // Storage unavailable: nothing was remembered.
  }
}

function readOwnedModerationSessionId(): string | null {
  try {
    return sessionStorage.getItem(OWNED_MODERATION_SESSION_KEY);
  } catch {
    return null;
  }
}

/**
 * The active moderation this tab may resume: same doc, same user and started
 * by this tab before a reload or reconnect.
 */
export function findResumableOwnedModeration(
  moderations: readonly ModerationInfo[],
  docId: string,
  userId: string
): ModerationInfo | undefined {
  const ownedSessionId = readOwnedModerationSessionId();
  if (!ownedSessionId) return undefined;
  return moderations.find(
    moderation =>
      moderation.sessionId === ownedSessionId &&
      moderation.docId === docId &&
      moderation.moderatorUserId === userId
  );
}
